import { useState } from "react";
import { toast } from "sonner";
import { Check, CheckCircle2, UtensilsCrossed } from "lucide-react";
import { MaskedLine, FadeIn } from "@/components/Reveal";
import { Field, SelectField, Option, SubmitButton } from "@/components/FormBits";
import { VENDOR_FORM_URL } from "@/constants/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  "Tell us about your kitchen and your dishes",
  "We review every restaurant before launch",
  "You hear from the Aflimart team as onboarding opens",
];

export default function RestaurantSignup() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    Object.keys(data).forEach((k) => { if (data[k] === "") delete data[k]; });
    setLoading(true);
    try {
      const res = await fetch(`${API}/restaurant-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("send failed");
      form.reset();
      setSent(true);
      toast.success("Application received.", {
        description: "Thank you — the Aflimart team will review your restaurant and be in touch.",
      });
    } catch {
      toast.error("Could not submit your application.", {
        description: "Please try again, or email aflimart.tech@gmail.com directly.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="restaurant-signup-page" className="relative overflow-hidden pb-24 pt-32 sm:pt-40 lg:pt-48">
      <div className="pointer-events-none absolute -top-24 left-[-8%] h-[26rem] w-[26rem] rounded-full bg-[#1E3A8A]/10 blur-[110px]" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-4 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-16">
        <div>
          <p className="eyebrow" data-testid="restaurant-signup-eyebrow">Vendor Onboarding · Foods</p>
          <h1 className="mt-8 font-sans text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Put your kitchen</MaskedLine>
            <MaskedLine delay={0.3} className="text-gold-gradient">on Aflimart.</MaskedLine>
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-[#64748B] sm:text-lg">
              Foods on Aflimart is built for restaurants and food businesses — jollof, suya, swallow,
              grills and more, ordered in a few taps and delivered to hungry customers. Register your
              restaurant for the launch.
            </p>
            <ul className="mt-10 space-y-4">
              {steps.map((s, i) => (
                <FadeIn key={s} delay={0.1 * i}>
                  <li data-testid={`restaurant-step-${i + 1}`} className="flex items-start gap-3 text-sm text-[#475569] sm:text-base">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#1E3A8A]/50">
                      <Check size={11} className="text-[#1E3A8A]" />
                    </span>
                    {s}
                  </li>
                </FadeIn>
              ))}
            </ul>
            <p className="mt-10 max-w-lg border-l border-[#1E3A8A]/25 pl-5 text-sm leading-relaxed text-[#94A3B8]" data-testid="restaurant-general-vendor-note">
              Not a restaurant? Food brands, packaged-food sellers and all other vendors use the{" "}
              <a href={VENDOR_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="restaurant-general-form-link" className="text-[#1E3A8A] underline-offset-4 transition-colors hover:text-[#1D4ED8] hover:underline">
                general vendor form
              </a>.
            </p>
          </FadeIn>
        </div>

        <FadeIn delay={0.35}>
          {sent ? (
            <div data-testid="restaurant-success" className="card-dark p-8 text-center sm:p-10 hover:transform-none">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-[#1E3A8A]/40 bg-[#1E3A8A]/10">
                <CheckCircle2 size={26} className="text-[#1E3A8A]" />
              </span>
              <h2 className="mt-6 font-sans text-2xl text-[#0F172A]">Application received.</h2>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#64748B]">
                Thank you — your restaurant details are with the Aflimart team. We review every
                kitchen before launch and will reach out as onboarding opens.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                data-testid="restaurant-submit-another-button"
                className="btn-ghost mt-8 !px-6 !py-3 !text-xs"
              >
                Register another restaurant
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="restaurant-signup-form" className="card-dark p-8 sm:p-10 hover:transform-none">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 rotate-45 items-center justify-center rounded-lg border border-[#1E3A8A]/40">
                  <UtensilsCrossed size={17} className="-rotate-45 text-[#1E3A8A]" />
                </span>
                <div>
                  <h2 className="font-sans text-2xl text-[#0F172A]">Restaurant onboarding</h2>
                  <p className="mt-1 text-sm text-[#94A3B8]">Essential details only — two minutes.</p>
                </div>
              </div>
              <div className="mt-8 space-y-7">
                <Field label="Business / restaurant name" name="restaurant_name" testId="restaurant-name-input" required placeholder="Mama Nkechi's Kitchen" />
                <Field label="Owner / contact name" name="contact_name" testId="restaurant-owner-input" required placeholder="Full name" />
                <Field label="Phone number" name="phone" type="tel" testId="restaurant-phone-input" required placeholder="+234 ..." />
                <Field label="Email" name="email" type="email" testId="restaurant-email-input" required placeholder="you@restaurant.com" />
                <Field label="Location" name="location" testId="restaurant-location-input" required placeholder="City, area — e.g. Lekki, Lagos" />
                <SelectField label="Food type / cuisine" name="cuisine" testId="restaurant-cuisine-select">
                  <Option value="">Select a cuisine</Option>
                  <Option value="Nigerian">Nigerian</Option>
                  <Option value="West African">West African</Option>
                  <Option value="Pan-African">Pan-African</Option>
                  <Option value="Grills & Suya">Grills & Suya</Option>
                  <Option value="Fast Food & Small Chops">Fast Food & Small Chops</Option>
                  <Option value="Bakery & Pastries">Bakery & Pastries</Option>
                  <Option value="Drinks & Smoothies">Drinks & Smoothies</Option>
                  <Option value="Continental">Continental</Option>
                  <Option value="Other">Other</Option>
                </SelectField>
                <Field label="Short description" name="description" testId="restaurant-description-input" textarea required placeholder="Tell us about your restaurant in a sentence or two" />
                <Field label="Menu or sample dishes (optional)" name="menu" testId="restaurant-menu-input" textarea placeholder="e.g. Jollof rice & chicken, pounded yam & egusi, suya platter..." />
                <Field label="Operating hours (optional)" name="hours" testId="restaurant-hours-input" placeholder="e.g. Mon–Sat, 10am–9pm" />
                <SelectField label="Delivery / fulfilment" name="fulfilment" testId="restaurant-fulfilment-select">
                  <Option value="">How would orders reach customers?</Option>
                  <Option value="own">I have my own delivery riders</Option>
                  <Option value="aflimart">I would use Aflimart's delivery</Option>
                  <Option value="both">Both — my riders and Aflimart's</Option>
                  <Option value="pickup">Pickup only</Option>
                </SelectField>
                <Field label="Social media or website (optional)" name="social" testId="restaurant-social-input" placeholder="Instagram handle or website link" />
                <SubmitButton loading={loading} testId="restaurant-submit-button">
                  Submit Application
                </SubmitButton>
              </div>
            </form>
          )}
        </FadeIn>
      </div>
    </section>
  );
}
